/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 * Merge property utility
 **/
define([], function(){

	class MergeUtil {
		_isJsonObject(obj) {
			return obj !== undefined && obj !== null && obj.constructor === Object;
		}

		_checkForCircularReference(value, knownReference) {
			for (let j=0; j < knownReference.length; j++) {
				if (value === knownReference[j]) {
					throw new Error('fail to merge due to circular reference');
				}
			}
		}

		_merge(left, right, knownLeftReferences, knownRightReferences) {
			this._checkForCircularReference(left, knownLeftReferences);
			this._checkForCircularReference(right, knownRightReferences);

			const merged = {};
			const leftKeys = Object.keys(left);
			const rightKeys = Object.keys(right);
			for (let j = 0; j < leftKeys.length; j++) {
				const key = leftKeys[j];
				if (!right.hasOwnProperty(key)) {
					if (this._isJsonObject(left[key])) {
						const knownReferences = [].concat(knownRightReferences);
						knownReferences.push(left);
						merged[key] = this._merge({}, left[key], [], knownReferences);
					} else {
						merged[key] = left[key];
					}
				}
			}
			for (let j = 0; j < rightKeys.length; j++) {
				const key = rightKeys[j];
				if (this._isJsonObject(right[key])) {
					if (this._isJsonObject(left[key])) {
						const newKnownLeftReferences = [].concat(knownLeftReferences);
						newKnownLeftReferences.push(left);
						const newKnownRightReferences = [].concat(knownRightReferences);
						newKnownRightReferences.push(right);
						merged[key] = this._merge(left[key], right[key], newKnownLeftReferences, newKnownRightReferences);
					} else {
						const knownReferences = [].concat(knownRightReferences);
						knownReferences.push(right);
						merged[key] = this._merge({}, right[key], [], knownReferences);
					}
				} else {
					merged[key] = right[key];
				}
			}
			return merged;
		}

		/**
		 * create a new json object that merges the right object into the left one; no references are preserved
		 * when a value is an class instance, it is considered as a leaf value,
		 * non-enumerable properties are ignored
		 * @param {object} left - object used as a left source for the merge
		 * @param {object} right - object used as the right source for the merge
		 * @return {object} - merged object
		 * @example
		 * const left = { c: { num: 123, str: '123', func: () => {}, inst: new (function(){ this._prop = 1; }), bool: true, arr: [0, 1] } };
		 * const right = { c: { num: 345, str: '345', func: () => {}, inst: new (function(){ this._anotherProp = 2; }), bool: false, arr: [1, 2] } };
		 * const merged = MergeUtil.mergeJson(left,right);
		 * //merged.c deep equal right.c
		 */
		mergeJson(left, right) {
			if (!this._isJsonObject(left) || !this._isJsonObject(right)) {
				throw new Error('sources are not pure json object');
			}

			return this._merge(left, right, [], []);
		}
	}
	return new MergeUtil();
});
